/**
 * Barre d'actions du devis / bon de commande / contrat de location.
 * Ce composant est appelé par QuotationComponent (dans app-action-bar)
 * et dépend de l'état de la commande (formGroup) pour projeter les boutons d'action
 * et les indicateurs logistiques (reste à livrer, poses et reprises).
 */
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SalesOrderState, SalesOrderAction, OrderLineType } from '@app/shared/constants-server';
import { isSealedOrder, tradeDocFor, TradeDoc, DocType, isRentalInvoice, isRentalCredit, RENTAL_INVOICE_NO_MANUAL_CREDIT, RENTAL_CREDIT_NO_CANCEL } from '@app/shared/constants';
import { Sales_order } from '@app/shared/_models/sales';

export interface DeliveryBadge {
  icon: string;
  text: string;
  cssClass: 'pose' | 'reprise' | 'mixed';
  count: number;
}

@Component({
  selector: 'app-quotation-action',
  templateUrl: './quotation-action.component.html',
  styleUrls: ['./quotation-action.component.scss'],
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, NgClass],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class QuotationActionComponent {
  @Input() public formGroup!: FormGroup; // The state of the order
  // Motif de fermeture de « Valider », calculé par l'écran (client et adresse d'une commande).
  // null = geste ouvert ; sinon le bouton est grisé et le texte part en infobulle.
  @Input() public blockReason: string | null = null;
  // Idem pour « Plannifier liv » : adresse de livraison et commande validée (QuotationComponent.deliveryBlockedReason).
  @Input() public deliveryBlockReason: string | null = null;
  // « Plannifier liv » existe-t-il sur ce document ? Faux sur la vente comptoir : le geste est
  // MASQUÉ, pas grisé — il n'y a aucun motif à afficher (QuotationComponent.deliveryVisible).
  @Input() public deliveryVisible = true;
  @Input() public remainder: number = 0; // Out-of-pocket remainder for patient
  @Output() submitEmitter = new EventEmitter<String>(); // Emits the triggered action

  public SalesOrderState = SalesOrderState;
  public SalesOrderAction = SalesOrderAction;

  get hasDeposit(): boolean {
    return !!(this.formGroup?.get('data')?.value?.deposit_order_id || this.formGroup?.value?.data?.deposit_order_id);
  }

  get state(): SalesOrderState | null {
    return this.formGroup?.get('state')?.value ?? null;
  }

  get docType(): DocType | null {
    return this.formGroup?.get('doc_type')?.value ?? this.formGroup?.value?.doc_type ?? null;
  }

  get isNegativeDraft(): boolean {
    if (this.state !== SalesOrderState.CREATED) return false;
    if (this.docType === DocType.FACTURE) return false;
    const amountTaxi = Number(this.formGroup?.get('amount_taxi')?.value ?? this.formGroup?.value?.amount_taxi ?? 0);
    return amountTaxi < 0;
  }

  // Avoir resté en préparation (2e étape refusée ou interrompue) : il se finit par le même geste explicite, jamais tout seul au rechargement.
  get isUnfinishedAvoir(): boolean {
    if (this.state !== SalesOrderState.PREPARATION || !this.isAvoir) return false;
    return Number(this.formGroup?.get('amount_taxi')?.value ?? this.formGroup?.value?.amount_taxi ?? 0) < 0;
  }

  get isAvoir(): boolean {
    return this.docType === DocType.AVOIR;
  }

  // La pièce qu'impriment et font signer les deux boutons (null : ni l'un ni l'autre) ; QuotationComponent refait le choix.
  // Un avoir n'est ni un devis ni un bon de commande : aucun document commercial intermédiaire à imprimer ni signer.
  get tradeDoc(): TradeDoc | null {
    if (this.isAvoir) return null;
    return tradeDocFor(this.state);
  }

  get ticketPrintLabel(): string {
    return this.isAvoir ? "Imprimer le ticket d'avoir" : 'Imprimer le ticket';
  }

  get printLabel(): string {
    return this.tradeDoc === 'devis' ? 'Imprimer le devis' : 'Imprimer le bon de commande';
  }

  // Le devis reste réimprimable après son acceptation, à côté du bon de commande (arbitrage JP du 29/09/2026) ; le serveur sert PRINT_QUOTE jusqu'à la livraison.
  get quoteReprintable(): boolean {
    return this.state === SalesOrderState.PREPARATION || this.state === SalesOrderState.DELIVERED;
  }

  get signLabel(): string {
    return this.tradeDoc === 'devis' ? 'Faire signer le devis' : 'Faire signer le bon de commande';
  }

  get id(): number | null {
    return this.formGroup?.get('id')?.value ?? null;
  }

  get isSealed(): boolean {
    return isSealedOrder(this.state);
  }

  get isCreditNoteCancellation(): boolean {
    return this.state === SalesOrderState.INVOICED || this.state === SalesOrderState.PAID;
  }

  get isLocation(): boolean {
    return this.formGroup?.get('doc_type')?.value === DocType.LOCATION;
  }

  // Facture de location émise ou avoir du moteur : ni annulation ni avoir ici, le dossier décide ; le bouton reste visible et grisé pour dire où aller.
  get rentalInvoiceLock(): string | null {
    if (!this.isCreditNoteCancellation) return null;
    const lines = this.formGroup?.get('lines')?.value ?? this.formGroup?.value?.lines;
    if (isRentalInvoice(this.docType, this.formGroup?.get('data')?.value, lines)) return RENTAL_INVOICE_NO_MANUAL_CREDIT;
    return isRentalCredit(this.docType, lines) ? RENTAL_CREDIT_NO_CANCEL : null;
  }

  get canApplyAvoir(): boolean {
    if (!this.id || this.id <= 0) return false;
    if (this.remainder <= 0) return false;
    const docType = this.formGroup?.get('doc_type')?.value;
    if ([DocType.AVOIR, DocType.FACTURE_ACOMPTE, DocType.LOCATION].includes(docType)) return false;
    // Une commande F2 n'encaisse rien avant sa facture (l'acompte passe par la FAC-AC) : l'avoir s'y impute une fois facturée.
    if (docType === DocType.FACTURE) return this.state === SalesOrderState.INVOICED;
    return [SalesOrderState.PREPARATION, SalesOrderState.DELIVERED, SalesOrderState.INVOICED].includes(this.state as SalesOrderState);
  }

  get deliveryLabel(): string {
    return this.isLocation ? 'Livraisons' : 'Plannifier liv';
  }

  /**
   * Calcule le badge dynamique du Reste À Livrer (RAL) sur les livraisons :
   * - Si uniquement des poses : 📦 `N équipement(s) à livrer`
   * - Si uniquement des reprises : 🔄 `N matériel(s) à récupérer`
   * - Si les deux : 🚚 `N mouvements logistiques à planifier`
   */
  get deliveryBadge(): DeliveryBadge | null {
    if (!this.formGroup) return null;

    const linesControl = this.formGroup.get('lines');
    const lines = linesControl ? linesControl.value : (this.formGroup.value?.lines ?? []);
    if (!Array.isArray(lines) || lines.length === 0) return null;

    const delControl = this.formGroup.get('lnk_deliveries');
    const deliveries = delControl ? delControl.value : (this.formGroup.value?.lnk_deliveries ?? []);

    let posesCount = 0;
    let reprisesCount = 0;

    for (const line of lines) {
      if (!line) continue;
      const typ = line.typ;
      // Ancienne ligne RENTAL_RETURN : ni à livrer ni à reprendre, la reprise se fait par −1 sur la ligne louée.
      if (typ === OrderLineType.DEPOSIT_DEDUCTION || typ === OrderLineType.RENTAL_RETURN) continue;

      // Reprise hors contrat (ligne louée négative, JP 30/10) : à reprendre tant que son BL −n ne la couvre pas ; une reprise sur BL n'est jamais un reste à livrer.
      if (typ === OrderLineType.RENTAL && +(line.qty ?? 0) < 0) {
        const back = (Array.isArray(deliveries) ? deliveries : []).flatMap((d: any) => d?.lnk_deliveryl ?? []).filter((dl: any) => dl?.id_order_l === line.id).reduce((s: number, dl: any) => s + Math.max(0, -(+(dl.qty ?? 0))), 0);
        reprisesCount += Math.max(0, -(+line.qty) - back);
        continue;
      }

      const qty = Math.max(0, +(line.qty ?? 0));
      if (qty <= 0) continue;

      let delivered = line.delivered != null ? +line.delivered : null;

      if (Array.isArray(deliveries) && deliveries.length > 0) {
        let sumDelivered = 0;
        let hasDelLine = false;
        for (const del of deliveries) {
          const dlines = del?.lnk_deliveryl;
          if (Array.isArray(dlines)) {
            for (const dl of dlines) {
              if (dl?.id_order_l === line.id) {
                sumDelivered += Math.max(0, +(dl.qty ?? 0));
                hasDelLine = true;
              }
            }
          }
        }
        if (hasDelLine) {
          delivered = sumDelivered;
        }
      }

      const deliveredQty = Math.max(0, delivered ?? 0);
      const ral = Math.max(0, qty - deliveredQty);

      if (ral > 0) posesCount += ral;
    }

    const totalRal = posesCount + reprisesCount;
    if (totalRal <= 0) return null;

    if (posesCount > 0 && reprisesCount === 0) {
      const text = this.isLocation ? `${posesCount} équipement${posesCount > 1 ? 's' : ''} à livrer` : `${posesCount} article${posesCount > 1 ? 's' : ''} à livrer`;
      return {
        icon: 'inventory_2',
        text,
        cssClass: 'pose',
        count: posesCount,
      };
    }

    if (posesCount === 0 && reprisesCount > 0) {
      return {
        icon: 'assignment_return',
        text: `${reprisesCount} matériel${reprisesCount > 1 ? 's' : ''} à récupérer`,
        cssClass: 'reprise',
        count: reprisesCount,
      };
    }

    return {
      icon: 'local_shipping',
      text: `${totalRal} mouvements logistiques à planifier`,
      cssClass: 'mixed',
      count: totalRal,
    };
  }

  /**
   * Checks if an action is permitted on the current order based on server capabilities
   * or local fallback evaluation.
   */
  can(action: SalesOrderAction): boolean {
    if (action === SalesOrderAction.QUOTE && (this.isNegativeDraft || this.isAvoir)) {
      return false;
    }
    const allowed = this.formGroup?.get('allowed_actions')?.value;
    if (Array.isArray(allowed) && allowed.length > 0) {
      return allowed.includes(action);
    }
    return Sales_order.computeLocalAllowedActions(this.state, this.formGroup?.get('addr_deliv')?.value, this.formGroup?.get('data')?.value, this.remainder, this.formGroup?.get('doc_type')?.value).includes(action);
  }

  onActionBarAction(action: string) {
    console.log('onActionBarAction: ' + action);
    this.submitEmitter.emit(action);
  }
}
